import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { Info } from 'lucide-react';
import Modal from 'ui/Modal';
import { Issue } from 'util/constants';
import IssueSearch, { Suggestion } from 'components/Issues/IssueSearch';
import { parseWikipediaData } from 'util/wikiUtils';

interface Props {
    solutionId: number;
    onIssueAdded: (value: Issue) => void;
    open: boolean;
    onClose: (value: boolean) => void;
    existingIssues?: Issue[];
}

const AddIssueToSolution: React.FC<Props> = ({ 
    solutionId, 
    onIssueAdded, 
    open, 
    onClose,
    existingIssues = [] 
}) => {
    const { currentUser, setShowAuthModal } = useUser();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isCreatingIssue, setIsCreatingIssue] = useState<boolean>(false);
    const [searchValidationMessage, setSearchValidationMessage] = useState<string>('');
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

    useEffect(() => {
        const isValidSubmit = () => {
            setSearchValidationMessage('');
            setError('');

            // Selected issue validation
            if (!selectedIssue && searchQuery.trim().length > 0) {
                setSearchValidationMessage('Please select an issue from the suggestions.');
                return false;
            }

            if (!selectedIssue) {
                return false;
            }

            // Not currently submitting
            if (isSubmitting || isCreatingIssue) {
                return false;
            }

            return true;
        };

        setSubmitEnabled(isValidSubmit());
    }, [selectedIssue, isSubmitting, isCreatingIssue, searchQuery]);

    const uploadReferenceImage = async (imageUrl: string, issueId: number) => {
        if (!imageUrl) return null;
        
        const imageResponse = await fetch(imageUrl);
      
        let contentType = imageResponse.headers.get('content-type');
  
        if (!contentType) {
          contentType = 'image/jpeg';
        }
        if (contentType.includes('image/svg+xml')) {
          contentType = 'image/svg';
        }
        if (contentType.includes('image/jpeg')) {
          contentType = 'image/jpg';
        }
  
        const blob = await imageResponse.blob();
            
        if (blob) {
            try {
                // Create form data
                const formData = new FormData();
                formData.append('image', blob, `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`);

                // Upload to our backend
                const response = await api.post(`/api/issues/image/${issueId}`, formData);
                return response.data.url;
            } catch (err) {
                console.error('Error uploading reference image:', err);
                return null;
            }
        }
    };

    const handleIssueSelect = (suggestion: Suggestion) => {
        setSelectedIssue(suggestion);
        setSearchQuery(suggestion.name);
        setSearchValidationMessage('');
    };

    const handleSearchChange = () => {
        setSelectedIssue(null);
        setSearchValidationMessage('');
    };

    const handleAddIssue = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }
        
        if (!submitEnabled || !selectedIssue) {
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            let issueToLink: Issue | null = null;

            if (selectedIssue.type === 'wiki') {
                setIsCreatingIssue(true);
                const wikiImg = await parseWikipediaData(selectedIssue.cname, true);
                try {
                    const response = await api.post(`/api/issues`, {
                        wikipedia_link: selectedIssue.link,
                        subject_cname: selectedIssue.cname,
                        subject_properties: {
                            ...selectedIssue.subject_properties,
                            wiki_parse_first_img: wikiImg.wiki_parse_first_img
                        },
                        user_id: currentUser?.id,
                    });
                    if (response.status === 201) {
                        issueToLink = response.data;
                        if (response.data.properties.subject_properties.wiki_parse_first_img) {
                            await uploadReferenceImage(response.data.properties.subject_properties.wiki_parse_first_img, response.data.id);
                        }
                    }
                } catch (error) {
                    console.error('Error creating issue:', error);
                    setError('Failed to create issue from Wikipedia entry');
                    setIsSubmitting(false);
                    setIsCreatingIssue(false);
                    return;
                } finally {
                    setIsCreatingIssue(false);
                }
            } else {
                // For existing issues, fetch the full issue data
                const response = await api.get(`/api/issues/id/${selectedIssue.id}`);
                issueToLink = response.data;
            }

            if (!issueToLink) {
                setError('Failed to process issue');
                setIsSubmitting(false);
                return;
            }

            await api.put(`/api/solutions/link/${solutionId}/${issueToLink.id}`);
            setSuccess('Issue linked successfully!');
            onIssueAdded(issueToLink);
            handleCloseModal();
        } catch (error: any) {
            console.error('Error adding issue to solution:', error);
            if (error.response?.status === 409) {
                setError('This issue is already linked to this solution.');
            } else {
                setError('Failed to add issue to solution. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        onClose(false);
        setSearchQuery('');
        setSelectedIssue(null);
        setError('');
        setSuccess('');
        setSearchValidationMessage('');
    };

    return (
        <Modal isOpen={open} onClose={handleCloseModal} title="Link issue">
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Search and select an issue that could be addressed by this solution. Issues in <i>italic</i> have not been added to the platform yet and will be added during submission.
                    </p>

                    <form onSubmit={(e) => { e.preventDefault(); handleAddIssue(); }} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <IssueSearch
                                inputType="add"
                                placeholder="Issue"
                                excludeIssueIds={existingIssues.map(issue => issue.id)}
                                className="w-full"
                                autoFocus={false}
                                fieldRequired={true}
                                validationMessage={searchValidationMessage}
                                validationState={searchValidationMessage ? 'invalid' : selectedIssue ? 'valid' : 'none'}
                                onSelect={handleIssueSelect}
                                onSearchChange={handleSearchChange}
                            />
                        </div>

                        {/* Error and Success Messages */}
                        {(error || success) && (
                            <div className="flex items-center space-x-2">
                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                {success && (
                                    <div className="flex items-center text-green-600 text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{success}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={handleCloseModal}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleAddIssue}
                        disabled={isSubmitting || !submitEnabled}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Submitting...' : isCreatingIssue ? 'Creating...' : 'Submit'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AddIssueToSolution;